import React, { ReactNode } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { Loader2 } from 'lucide-react';
import { DuckLoadingScreen } from '@/components/DuckLoadingScreen';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { hasVisitedMspView } from '@/utils/mspViewSession';

interface TenancyGuardProps {
  children: ReactNode;
  loadingComponent?: ReactNode;
  errorComponent?: (error: Error) => ReactNode;
}

/**
 * A component that ensures tenancy is loaded before rendering its children.
 * Shows a loading state while tenancy is being loaded and an error state if loading fails.
 * Also handles MSP-only tenants by redirecting to tenancy management.
 */
export const TenancyGuard: React.FC<TenancyGuardProps> = ({
  children,
  loadingComponent,
  errorComponent,
}) => {
  const { loading, activeTenancy, tenancies, setActiveTenancy } = useTenancy();
  const navigate = useNavigate();

  // Check if msp-view has been visited in the current session
  const hasVisitedMspViewSession = hasVisitedMspView();

  // Redirect MSP-only tenants to tenancy management
  useEffect(() => {
    if (!loading && activeTenancy && activeTenancy.isMSPOnly) {
      // Only redirect if not already on tenancy management page and haven't visited msp-view this session
      if (window.location.pathname !== '/msp-view' && !hasVisitedMspViewSession) {
        console.log('[TenancyGuard] MSP-only tenant detected, redirecting to tenancy management');
        navigate('/msp-view');
      }
    }
  }, [loading, activeTenancy, navigate, hasVisitedMspViewSession]);

  // Default error component
  const defaultError = (error: Error) => (
    <div className="container mx-auto p-4">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error loading tenancy</AlertTitle>
        <AlertDescription>
          {error.message || 'Failed to load tenancy data. Please try refreshing the page.'}
        </AlertDescription>
      </Alert>
    </div>
  );

  // Handle different states without early returns
  let content;

  if (loading) {
    content = <>{loadingComponent || <DuckLoadingScreen />}</>;
  } else if (activeTenancy && activeTenancy.id) {
    // For MSP-only tenants, show children only on tenancy management page
    if (activeTenancy.isMSPOnly && window.location.pathname !== '/msp-view') {
      content = <>{loadingComponent || <DuckLoadingScreen />}</>;
    } else {
      content = <>{children}</>;
    }
  } else {
    // Check if we have a cached tenancy before redirecting
    let cachedTenancy;
    try {
      // Use the same cache key as TenancyProvider
      const data = sessionStorage.getItem('active_tenancy_cache_v1');
      cachedTenancy = data ? JSON.parse(data) : undefined;
    } catch { }
    
    // Debug logging
    console.log('[TenancyGuard] No active tenancy - Debug info:', {
      tenanciesLength: tenancies.length,
      pathname: window.location.pathname,
      firstTenancyIsMSPOnly: tenancies[0]?.isMSPOnly,
      cachedTenancy: cachedTenancy?.id
    });
    
    // If we have a cached tenancy that exists in our list, restore it
    if (cachedTenancy && tenancies.find(t => t.id === cachedTenancy.id)) {
      console.log('[TenancyGuard] Restoring cached tenancy:', cachedTenancy.id);
      setActiveTenancy(cachedTenancy);
      content = <>{loadingComponent || <DuckLoadingScreen />}</>;
    } else if (tenancies.length === 1 && !tenancies[0].isMSPOnly) {
      // Auto-select the only tenancy if it's not MSP-only
      setActiveTenancy(tenancies[0]);
      content = <>{loadingComponent || <DuckLoadingScreen />}</>;
    } else if (tenancies.length === 1 && tenancies[0].isMSPOnly && window.location.pathname !== '/msp-view' && !hasVisitedMspViewSession) {
      // If the only tenancy is MSP-only, redirect to tenancy management (only if haven't visited this session)
      console.log('[TenancyGuard] Redirecting single MSP-only user to /msp-view');
      navigate('/msp-view');
      content = <>{loadingComponent || <DuckLoadingScreen />}</>;
    } else if (window.location.pathname !== '/msp-view' && !hasVisitedMspViewSession) {
      // If user has multiple tenancies and no valid cached tenancy, redirect to tenancy management (only if haven't visited this session)
      console.log('[TenancyGuard] No cached tenancy, redirecting to /msp-view');
      navigate('/msp-view');
      content = <>{loadingComponent || <DuckLoadingScreen />}</>;
    } else {
      console.log('[TenancyGuard] Showing no tenancy error on /msp-view');
      content = (
        <div className="container mx-auto p-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No tenancy selected</AlertTitle>
            <AlertDescription>
              Please select a tenancy to continue.
            </AlertDescription>
          </Alert>
        </div>
      );
    }
  }

  return content;
};

/**
 * Higher-order component for class components
 */
export function withTenancyGuard<T>(
  WrappedComponent: React.ComponentType<T>,
  options: Omit<TenancyGuardProps, 'children'> = {}
) {
  return (props: T) => (
    <TenancyGuard {...options}>
      <WrappedComponent {...(props as T)} />
    </TenancyGuard>
  );
}
